import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { required, validationContext } from 'validx'
import type { SnackbarFactory } from '@/features/shell'
import type { AvailableBookkeepingSoftwareType } from '@/features/bookkeeping/platform-connections/available-bookkeeping-software'

export interface BookkeepingOtherConnectionDialogViewModelOptions {
  /**
   * The function to call when the user selects a platform connection.
   */
  onAction: (software: AvailableBookkeepingSoftwareType) => Promise<unknown>
}

export class BookkeepingOtherConnectionDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   *  The modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * Validation.
   */
  validation = validationContext(this, {
    bookkeepingSoftware: [required('Software is required')],
  })

  /**
   * The bookkeeping software type.
   */
  @observable
  accessor bookkeepingSoftware: AvailableBookkeepingSoftwareType = 'Freshbooks'

  constructor(
    private readonly _options: BookkeepingOtherConnectionDialogViewModelOptions,
    private readonly _newSnackbar: SnackbarFactory,
  ) {}

  /** @inheritdoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritdoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    this.validation.reset()
    this.bookkeepingSoftware = 'Freshbooks'
    this.modal.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  /**
   * Saves the selected bookkeeping software.
   */
  save = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      await this._options.onAction(this.bookkeepingSoftware)
      this.close()
    } catch (err: unknown) {
      const snackbar = this._newSnackbar()
      snackbar.fromError(err)
    }
  })
}
